import { add, getUnixTime } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';

const easternTimeZone = 'America/New_York';

const getDateValues = ({ dateInput, timeZone, fullDateFormat = 'yyyy-MM-dd' }) => {
    const dateObject = new Date(dateInput);
    const dateZoned = utcToZonedTime(dateObject, timeZone);

    return {
        fullText: format(dateZoned, fullDateFormat, { timeZone }),
        day: format(dateZoned, 'd', { timeZone }),
        dayOfWeek: format(dateZoned, 'EEEE', { timeZone }),
        monthValue: format(dateZoned, 'MM-yyyy', { timeZone }),
        monthText: format(dateZoned, 'MMMM', { timeZone }),
        year: format(dateZoned, 'yyyy', { timeZone }),
    };
};

export const formatDateInEastern = (date, formatString) => {
    const dateObject = new Date(date);
    const dateZoned = utcToZonedTime(dateObject, easternTimeZone);
    return format(dateZoned, formatString, { timeZone: easternTimeZone });
};

export const formatDateInTimeZone = (date, formatString, timeZone) => {
    const dateObject = new Date(date);
    const dateZoned = utcToZonedTime(dateObject, timeZone);
    return format(dateZoned, formatString, { timeZone });
};

export const getDateRangeTextInEastern = (start, end) => {
    // dates don't really have timezones. All dates are displayed in eastern time.
    // e.g. if date is at noon (EST), but the event is in california,
    // the date displays as noon, but it's in Cali so it's interpreted as noon PT
    const timeZone = 'America/New_York';
    // could be formatted either way
    // 2018-03-21T04:00:00+00:00
    // 2018-03-21T04:00:00+0000
    const startDate = new Date(start);
    const startDateNy = utcToZonedTime(startDate, timeZone);
    const startFullText = format(startDateNy, 'yyyy-MM-dd', { timeZone });
    const startDay = format(startDateNy, 'd', { timeZone });
    const startMonthValue = format(startDateNy, 'MM-yyyy', { timeZone });
    const startMonthText = format(startDateNy, 'MMMM', { timeZone });
    const startYear = format(startDateNy, 'yyyy', { timeZone });

    if (!end) {
        return `${startMonthText} ${startDay}, ${startYear}`;
    }

    const endDate = new Date(end);
    const endDateNy = utcToZonedTime(endDate, timeZone);
    const endFullText = format(endDateNy, 'yyyy-MM-dd', { timeZone });
    const endDay = format(endDateNy, 'd', { timeZone });
    const endMonthValue = format(endDateNy, 'MM-yyyy', { timeZone });
    const endMonthText = format(endDateNy, 'MMMM', { timeZone });
    const endYear = format(endDateNy, 'yyyy', { timeZone });

    if (startFullText === endFullText) {
        return `${startMonthText} ${startDay}, ${startYear}`;
    }
    if (startMonthValue === endMonthValue) {
        return `${startMonthText} ${startDay}-${endDay}, ${startYear}`;
    }
    if (startYear === endYear) {
        return `${startMonthText} ${startDay} - ${endMonthText} ${endDay}, ${startYear}`;
    }
    return `${startMonthText} ${startDay}, ${startYear} - ${endMonthText} ${endDay}, ${endYear}`;
};

export const getDateRangeTextLong = (start, end) => {
    const startDate = getDateValues({ dateInput: start, timeZone: easternTimeZone });
    const startDateFormatted = `${startDate.dayOfWeek}, ${startDate.monthText} ${startDate.day}`;

    if (!end) {
        return startDateFormatted;
    }

    const endDate = getDateValues({ dateInput: end, easternTimeZone });
    const endDateFormatted = `${endDate.dayOfWeek}, ${endDate.monthText} ${endDate.day}`;

    if (startDate.fullText === endDate.fullText) {
        return startDateFormatted;
    }

    if (startDate.year === endDate.year) {
        return `${startDateFormatted} - ${endDateFormatted}`;
    }

    return `${startDateFormatted}, ${startDate.year} - ${endDateFormatted}, ${endDate.year}`;
};

export const getTimeRangeText = (start, end) => {
    const startDateObject = new Date(start);
    const startDateZoned = utcToZonedTime(startDateObject, easternTimeZone);
    const startTime = format(startDateZoned, 'h:mm aaa');

    if (!end) {
        return startTime;
    }

    const endDateObject = new Date(end);
    const endDateZoned = utcToZonedTime(endDateObject, easternTimeZone);
    const endTime = format(endDateZoned, 'h:mm aaa');
    return `${startTime} – ${endTime}`;
};

// 'YYYY-MM-DD'
export const getDateFromApiDate = (apiDate) => {
    if (!apiDate) return null;

    const parts = apiDate.split('-');
    return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2]));
};

export const getDatesForDateRange = ({
    startDate,
    endDate,
    dateFormatLong = 'EEEE, MMMM d',
    dateFormatShort = 'yyyy-MM-dd',
}) => {
    const dayArray = [
        {
            long: formatDateInEastern(startDate, dateFormatLong),
            short: formatDateInEastern(startDate, dateFormatShort),
            date: startDate,
        },
    ];

    if (!endDate) {
        return dayArray;
    }

    const endDateShort = formatDateInEastern(endDate, dateFormatShort);
    let movingDate = startDate;
    let movingDateLong = formatDateInEastern(movingDate, dateFormatLong);
    let movingDateShort = formatDateInEastern(movingDate, dateFormatShort);

    while (movingDateShort !== endDateShort) {
        movingDate = add(movingDate, { days: 1 });
        movingDateLong = formatDateInEastern(movingDate, dateFormatLong);
        movingDateShort = formatDateInEastern(movingDate, dateFormatShort);

        dayArray.push({
            long: movingDateLong,
            short: movingDateShort,
            date: movingDate,
        });
    }

    return dayArray;
};

export const getTodayUnix = () => {
    const today = new Date();
    const todayDay = today.getDate();
    const todayMonth = today.getMonth();
    const todayYear = today.getFullYear();
    return getUnixTime(new Date(todayYear, todayMonth, todayDay));
};

export const getUnixTimestamp = () => getUnixTime(utcToZonedTime(new Date(), easternTimeZone));

export const shortenFullTimestamp = (fullTimestamp) => Math.round(fullTimestamp / 1000);
